import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Window, WindowHeader, WindowContent, Hourglass, Button, GroupBox, Checkbox, Select, Frame } from 'react95';
import classNames from 'classnames';
import { concat, defaultTo, get, isEmpty, map } from 'lodash-es';
import { userSelector } from '../../selectors/authSelectors';
import { opsInfoVisibleSelector } from '../../selectors/taskDetailSelectors';
import { setOpsInfoVisible } from '../../actions/taskDetailActions';
import { EXTENSION_STATUSES } from '../../global/Environment';
import './OpsInfoTable.scss';
import { useGetTaskQuery, useUpdateExtensionStatusMutation, useUpdateOpsStatusMutation } from '../../api/opsSystemApi';
import { useParams } from 'react-router-dom';
import { useGetTaxProsQuery, useUpdateTaxProAssignmentMutation } from '../../api/taxProConsultationApi';
import { useGetIdVerificationQuery, useResetIdVerificationMutation } from '../../api/idVerificationApi';
import { Box, Typography } from '@mui/material';

const OpsInfoTable = ({ user, opsInfoVisible, setOpsInfoVisible }) => {
  const [updateExtensionStatus] = useUpdateExtensionStatusMutation();
  const [updateTaxProAssignment] = useUpdateTaxProAssignmentMutation();

  const { taskId } = useParams();
  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId);
  const { data: taxPros, isLoading: areTaxProsLoading } = useGetTaxProsQuery();

  const userId = get(task, ['user', 'id']);

  const [updateOpsStatus] = useUpdateOpsStatusMutation();

  const taxProAssignmentValue = useMemo(() => {
    return defaultTo(get(task, ['taxProAssignment', 'taxProEmail']), '');
  }, [task]);

  const taxProAssignmentOptions = useMemo(() => {
    const taxProsOptions = map(taxPros, (taxPro) => ({ label: taxPro.name, value: taxPro.email }));

    if (isEmpty(taxProAssignmentValue)) {
      return concat(taxProsOptions, { label: 'Unassigned', value: '' });
    }

    return taxProsOptions;
  }, [taxPros, taxProAssignmentValue]);

  if (areTaxProsLoading) {
    return null;
  }

  const instantReview = get(task, ['ops_status', 'instantReview']) || false;
  const manualAdjustment = get(task, ['ops_status', 'manualAdjustment']) || false;
  const federalDrakeNotes = isEmpty(task.drake_notes) ? 'N/A' : task.drake_notes.join(', ');
  const stateDrakeNotes = isEmpty(task.state_drake_notes) ? 'N/A' : task.state_drake_notes.join(', ');
  const instantReviewNotes = get(task, ['ops_status', 'instantReviewFailureReasons', 0])
    ? task.ops_status.instantReviewFailureReasons.join(', ')
    : 'N/A';
  const automationCheckNotes = get(task, ['ops_status', 'failedAutomationChecks', 0])
    ? task.ops_status.failedAutomationChecks.join(', ')
    : 'N/A';

  return (
    <Window className={classNames('ops-info-table-window', { hidden: !opsInfoVisible })}>
      <div className='ops-info-table-window-inner'>
        <WindowHeader className='ops-info-table-header'>
          <div>
            <span>Ops Details</span>
            {isTaskLoading && <Hourglass size={24} />}
          </div>
          <Button primary square size='sm' variant='flat' onClick={() => setOpsInfoVisible(!opsInfoVisible)}>
            -
          </Button>
        </WindowHeader>
        <WindowContent className='ops-info-table-content'>
          <GroupBox className='ops-info-table-content-group'>
            <Checkbox
              value='manualAdjustment'
              label='Manual adjustment by premium preparer'
              checked={manualAdjustment}
              onChange={() => updateOpsStatus({ taskId: task.id, opsStatus: { manualAdjustment: !manualAdjustment } })}
            />
            <GroupBox label='Extension status'>
              <Select
                width={'100%'}
                menuMaxHeight={300}
                options={EXTENSION_STATUSES}
                value={task.extension_status || ''}
                onChange={(opt) =>
                  updateExtensionStatus({
                    taskId: task.id,
                    selected: opt.value,
                    reviewer: user.email
                  })
                }
                disabled={!task.user?.id}
              />
            </GroupBox>

            <GroupBox label='Federal Drake notes'>
              <Frame className='ops-info-table-content-text' variant='field'>
                {federalDrakeNotes}
              </Frame>
            </GroupBox>

            <GroupBox label='State Drake notes'>
              <Frame className='ops-info-table-content-text' variant='field'>
                {stateDrakeNotes}
              </Frame>
            </GroupBox>

            <IdVerificationInfo userId={userId} />
          </GroupBox>

          <GroupBox className='ops-info-table-content-group'>
            <Checkbox value='instantReview' label='Instant review' checked={instantReview} disabled />

            <GroupBox label='Assigned Tax Pro'>
              <Select
                width={'100%'}
                menuMaxHeight={300}
                options={taxProAssignmentOptions}
                value={taxProAssignmentValue}
                onChange={(opt) =>
                  updateTaxProAssignment({
                    userId: task.user.id,
                    taxProEmail: opt.value,
                    taxProName: opt.label,
                    taskId: task.id
                  })
                }
                disabled={!task.user?.id}
              />
            </GroupBox>

            <GroupBox label='Instant review failure reasons'>
              <Frame className='ops-info-table-content-text' variant='field'>
                {instantReviewNotes}
              </Frame>
            </GroupBox>

            <GroupBox label='Automated check failure reasons'>
              <Frame className='ops-info-table-content-text' variant='field'>
                {automationCheckNotes}
              </Frame>
            </GroupBox>
          </GroupBox>
        </WindowContent>
      </div>
    </Window>
  );
};

const IdVerificationInfo = ({ userId }) => {
  const RESULT_COLORS = { true: 'green', false: 'red' };
  const getResultLabel = (value) => (value === true ? 'Passed' : value === false ? 'Failed' : 'Incomplete');

  const IdVerificationLabel = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography variant='body1'>{label}</Typography>
      <Typography variant='body1' sx={{ fontWeight: 'bold', color: RESULT_COLORS[value] || 'gray' }}>
        {getResultLabel(value)}
      </Typography>
    </Box>
  );

  const { data: idVerificationData, isLoading: isIdVerificationLoading } = useGetIdVerificationQuery(userId, {
    skip: !userId
  });
  const [resetIdVerification, { isLoading: isResettingIdVerification }] = useResetIdVerificationMutation();

  return (
    <GroupBox className='ops-info-table-content-id-verification' label='ID verification'>
      {isIdVerificationLoading ? (
        <Hourglass size={24} />
      ) : (
        <>
          <IdVerificationLabel label='ID match' value={get(idVerificationData, 'idMatchResult')} />
          <IdVerificationLabel label='ID verification' value={get(idVerificationData, 'idVerificationResult')} />
          <Button size='sm' disabled={isResettingIdVerification || !userId} onClick={() => resetIdVerification(userId)}>
            Reset
          </Button>
        </>
      )}
    </GroupBox>
  );
};

const mapStateToProps = (state) => ({
  user: userSelector(state),
  opsInfoVisible: opsInfoVisibleSelector(state)
});

const mapDispatchToProps = {
  setOpsInfoVisible
};

export default connect(mapStateToProps, mapDispatchToProps)(OpsInfoTable);
